import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { entityAPI, userAPI, foodItemAPI, recommendationAPI, surpriseAPI, dishRequestAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Search,
  MapPin,
  ChefHat,
  Store,
  ShoppingCart,
  Plus,
  Minus,
  Star,
  Sparkles,
  Filter,
  Sun,
  Coffee,
  Moon,
  Cookie,
  Wand2,
  MessageSquarePlus,
  Send,
  X
} from 'lucide-react';
import BuyerLayout from '../../components/layouts/BuyerLayout';

const FOOD_PLACEHOLDER = "https://images.unsplash.com/photo-1672477179695-7276b0602fa9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjBmb29kJTIwc3ByZWFkJTIwdG9wJTIwdmlld3xlbnwwfHx8fDE3Njc2MDUxOTl8MA&ixlib=rb-4.1.0&q=85&w=400";

const MEAL_TYPE_CONFIG = {
  breakfast: { label: 'Breakfast', icon: Coffee, color: 'bg-amber-100 text-amber-800' },
  lunch: { label: 'Lunch', icon: Sun, color: 'bg-orange-100 text-orange-800' },
  dinner: { label: 'Dinner', icon: Moon, color: 'bg-indigo-100 text-indigo-800' },
  snacks: { label: 'Snacks', icon: Cookie, color: 'bg-pink-100 text-pink-800' },
  beverages: { label: 'Beverages', icon: Coffee, color: 'bg-cyan-100 text-cyan-800' },
  all_day: { label: 'All Day', icon: Star, color: 'bg-green-100 text-green-800' },
};

export default function BuyerHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items: cartItems, addItem, updateQuantity, setCartEntity, getItemCount } = useCart();
  
  const [entityName, setEntityName] = useState('');
  const [sellers, setSellers] = useState([]);
  const [foodItems, setFoodItems] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Surprise Me state
  const [surpriseItem, setSurpriseItem] = useState(null);
  const [surpriseLoading, setSurpriseLoading] = useState(false);
  const [showSurpriseModal, setShowSurpriseModal] = useState(false);
  
  // Dish Request state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({
    title: '',
    description: '',
    meal_type: 'all_day',
    budget_min: '',
    budget_max: '',
    target_seller_id: 'broadcast'
  });
  const [requestLoading, setRequestLoading] = useState(false);

  useEffect(() => {
    if (user?.entity_id) {
      setCartEntity(user.entity_id);
      loadEntityName();
      loadData();
    }
  }, [user?.entity_id]);

  const loadEntityName = async () => {
    try {
      const response = await entityAPI.getAll();
      const entity = response.data.find(e => e.id === user.entity_id);
      if (entity) {
        setEntityName(`${entity.name}, ${entity.city}`);
      }
    } catch (error) {
      console.error('Failed to load entity name');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [sellersRes, itemsRes] = await Promise.all([
        userAPI.getSellers(user.entity_id),
        foodItemAPI.getAll({ entity_id: user.entity_id })
      ]);
      setSellers(sellersRes.data);
      setFoodItems(itemsRes.data);
      
      if (user?.id) {
        loadRecommendations();
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadRecommendations = async () => {
    try {
      const response = await recommendationAPI.get(user.id, 'browsing home page');
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error('Recommendations error:', error);
    }
  };

  // Debounced search function
  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      // Reset to all items
      loadData();
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await foodItemAPI.search(user.entity_id, query);
      setFoodItems(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearchLoading(false);
    }
  }, [user?.entity_id]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== '') {
        handleSearch(searchQuery);
      } else if (user?.entity_id) {
        loadData();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const getCartQuantity = (itemId) => {
    const cartItem = cartItems.find(i => i.id === itemId);
    return cartItem?.quantity || 0;
  };

  const handleAddToCart = (item) => {
    addItem(item);
    toast.success(`Added ${item.name} to cart`);
  };

  // Surprise Me handler
  const handleSurpriseMe = async () => {
    setSurpriseLoading(true);
    try {
      const response = await surpriseAPI.getSurpriseItem(user.entity_id);
      setSurpriseItem(response.data);
      setShowSurpriseModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No items available for surprise');
    } finally {
      setSurpriseLoading(false);
    }
  };

  // Dish Request handlers
  const handleRequestSubmit = async () => {
    if (!requestForm.title.trim() || !requestForm.description.trim()) {
      toast.error('Please fill in title and description');
      return;
    }
    
    setRequestLoading(true);
    try {
      const data = {
        title: requestForm.title,
        description: requestForm.description,
        meal_type: requestForm.meal_type,
        budget_min: requestForm.budget_min ? parseFloat(requestForm.budget_min) : null,
        budget_max: requestForm.budget_max ? parseFloat(requestForm.budget_max) : null,
        target_seller_id: requestForm.target_seller_id === 'broadcast' ? null : requestForm.target_seller_id
      };
      
      await dishRequestAPI.create(data, user.id);
      toast.success(requestForm.target_seller_id === 'broadcast'
        ? 'Request broadcast to all cooks!' 
        : 'Request sent to the cook!');
      setShowRequestModal(false);
      setRequestForm({
        title: '',
        description: '',
        meal_type: 'all_day',
        budget_min: '',
        budget_max: '',
        target_seller_id: 'broadcast'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setRequestLoading(false);
    }
  };

  const categories = ['all', ...new Set(foodItems.map(item => item.category))];
  
  const filteredItems = foodItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesCategory;
  });

  return (
    <BuyerLayout>
      <div className="space-y-6 pb-mobile-nav">
        {/* Community Info (Fixed - no selector) */}
        <div className="bg-card rounded-2xl p-4 shadow-sm border border-border/50">
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-primary" />
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Your Community</p>
              <p className="font-semibold text-foreground">{entityName || 'Loading...'}</p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-input-wrapper">
          <Search />
          <Input
            data-testid="search-input"
            placeholder="Search for dishes, categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-12 rounded-xl pl-10"
          />
          {searchLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        {/* Action Buttons - Surprise Me & Request Dish */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={handleSurpriseMe}
            disabled={surpriseLoading}
            className="h-14 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg"
            data-testid="surprise-me-btn"
          >
            {surpriseLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Wand2 className="w-5 h-5 mr-2" />
            )}
            Surprise Me!
          </Button>
          <Button
            onClick={() => setShowRequestModal(true)}
            variant="outline"
            className="h-14 rounded-xl border-2 border-primary text-primary hover:bg-primary/10"
            data-testid="request-dish-btn"
          >
            <MessageSquarePlus className="w-5 h-5 mr-2" />
            Request a Dish
          </Button>
        </div>

        {/* AI Recommendations */}
        {recommendations.length > 0 && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <span className="font-semibold text-sm">Recommended for You</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {recommendations[0]}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`category-pill whitespace-nowrap ${selectedCategory === category ? 'active' : ''}`}
              data-testid={`category-${category}`}
            >
              {category === 'all' ? 'All Items' : category}
            </button>
          ))}
        </div>

        {/* Sellers Section */}
        {!loading && sellers.length > 0 && (
          <section>
            <div className="section-header">
              <h2 className="section-title">Available Cooks & Vendors</h2>
              <span className="text-sm text-muted-foreground">{sellers.length} available</span>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4">
              {sellers.map((seller) => (
                <Card 
                  key={seller.id} 
                  className="min-w-[140px] card-hover cursor-pointer"
                  onClick={() => navigate(`/buyer/seller/${seller.id}`)}
                  data-testid={`seller-card-${seller.id}`}
                >
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
                      {seller.role === 'seller' ? (
                        <ChefHat className="w-6 h-6 text-primary" />
                      ) : (
                        <Store className="w-6 h-6 text-primary" />
                      )}
                    </div>
                    <p className="font-semibold text-sm truncate">{seller.name}</p>
                    <Badge variant="outline" className="mt-1 text-xs">
                      {seller.role === 'seller' ? 'Home Cook' : 'Vendor'}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Food Items Grid */}
        <section>
          <div className="section-header">
            <h2 className="section-title">Today's Menu</h2>
            <Button variant="ghost" size="sm" className="text-muted-foreground">
              <Filter className="w-4 h-4 mr-1" />
              Filter
            </Button>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="h-40 w-full" />
                  <CardContent className="p-4 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                    <Skeleton className="h-8 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="empty-state">
              <ChefHat className="empty-state-icon" />
              <h3 className="font-heading text-lg font-semibold">No items found</h3>
              <p className="text-muted-foreground text-sm">
                {searchQuery ? 'Try a different search term' : 'No food items available in your community yet'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredItems.map((item) => {
                const cartQty = getCartQuantity(item.id);
                const mealConfig = MEAL_TYPE_CONFIG[item.meal_type] || MEAL_TYPE_CONFIG.all_day;
                const MealIcon = mealConfig.icon;
                
                return (
                  <Card key={item.id} className="overflow-hidden card-hover" data-testid={`food-item-${item.id}`}>
                    <div className="relative h-40">
                      <img
                        src={item.image_url || FOOD_PLACEHOLDER}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = FOOD_PLACEHOLDER;
                        }}
                      />
                      <div className="absolute top-2 left-2 flex gap-1">
                        <span className={item.is_vegetarian ? 'veg-badge' : 'non-veg-badge'}></span>
                      </div>
                      {item.is_free && (
                        <span className="absolute top-2 right-2 free-badge">FREE</span>
                      )}
                      {/* Meal Type Badge */}
                      <div className={`absolute bottom-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${mealConfig.color}`}>
                        <MealIcon className="w-3 h-3" />
                        {mealConfig.label}
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-1">
                        <h3 className="font-semibold text-foreground truncate flex-1">{item.name}</h3>
                        <span className="price-tag ml-2">
                          {item.is_free ? 'Free' : `₹${item.price}`}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{item.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className="text-xs">
                            {item.seller_type === 'seller' ? (
                              <><ChefHat className="w-3 h-3 mr-1" /> {item.seller_name}</>
                            ) : (
                              <><Store className="w-3 h-3 mr-1" /> {item.seller_name}</>
                            )}
                          </Badge>
                        </div>
                        {cartQty > 0 ? (
                          <div className="quantity-selector">
                            <button 
                              onClick={() => updateQuantity(item.id, cartQty - 1)}
                              data-testid={`decrease-${item.id}`}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span data-testid={`qty-${item.id}`}>{cartQty}</span>
                            <button 
                              onClick={() => updateQuantity(item.id, cartQty + 1)}
                              data-testid={`increase-${item.id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleAddToCart(item)}
                            className="rounded-full"
                            data-testid={`add-to-cart-${item.id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        {/* Floating Cart Button */}
        {getItemCount() > 0 && (
          <button
            onClick={() => navigate('/buyer/cart')}
            className="fab"
            data-testid="cart-fab"
          >
            <ShoppingCart className="w-6 h-6" />
            <span className="cart-badge">{getItemCount()}</span>
          </button>
        )}
      </div>

      {/* Surprise Me Modal */}
      <Dialog open={showSurpriseModal} onOpenChange={setShowSurpriseModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-500" />
              Your Surprise Dish!
            </DialogTitle>
            <DialogDescription>
              {surpriseItem?.message}
            </DialogDescription>
          </DialogHeader>
          {surpriseItem && (
            <div className="space-y-4">
              <div className="relative rounded-xl overflow-hidden">
                <img
                  src={surpriseItem.image_url || FOOD_PLACEHOLDER}
                  alt={surpriseItem.name}
                  className="w-full h-48 object-cover"
                  onError={(e) => { e.target.src = FOOD_PLACEHOLDER; }}
                />
                <div className="absolute top-2 left-2">
                  <span className={surpriseItem.is_vegetarian ? 'veg-badge' : 'non-veg-badge'}></span>
                </div>
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold">{surpriseItem.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{surpriseItem.description}</p>
                <div className="flex items-center justify-between mt-3">
                  <Badge variant="outline">
                    {surpriseItem.seller_type === 'seller' ? (
                      <><ChefHat className="w-3 h-3 mr-1" /> {surpriseItem.seller_name}</>
                    ) : (
                      <><Store className="w-3 h-3 mr-1" /> {surpriseItem.seller_name}</>
                    )}
                  </Badge>
                  <span className="price-tag text-lg">
                    {surpriseItem.is_free ? 'Free' : `₹${surpriseItem.price}`}
                  </span>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowSurpriseModal(false)}
                >
                  Try Again
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => {
                    handleAddToCart(surpriseItem);
                    setShowSurpriseModal(false);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add to Cart
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Request Dish Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-primary" />
              Request a Dish
            </DialogTitle>
            <DialogDescription>
              Can&apos;t find what you&apos;re looking for? Request it from our cooks!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label htmlFor="request-title">What do you want?</Label>
              <Input
                id="request-title"
                placeholder="e.g., Hyderabadi Biryani, Masala Dosa..."
                value={requestForm.title}
                onChange={(e) => setRequestForm(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="request-desc">Description</Label>
              <Textarea
                id="request-desc"
                placeholder="Any specific requirements? (spice level, quantity, etc.)"
                value={requestForm.description}
                onChange={(e) => setRequestForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="request-meal">Meal Type</Label>
                <Select 
                  value={requestForm.meal_type}
                  onValueChange={(value) => setRequestForm(prev => ({ ...prev, meal_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="breakfast">Breakfast</SelectItem>
                    <SelectItem value="lunch">Lunch</SelectItem>
                    <SelectItem value="dinner">Dinner</SelectItem>
                    <SelectItem value="snacks">Snacks</SelectItem>
                    <SelectItem value="all_day">Any Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="target-seller">Request From</Label>
                <Select 
                  value={requestForm.target_seller_id}
                  onValueChange={(value) => setRequestForm(prev => ({ ...prev, target_seller_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Cooks" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="broadcast">All Cooks (Broadcast)</SelectItem>
                    {sellers.map(seller => (
                      <SelectItem key={seller.id} value={seller.id}>
                        {seller.name} ({seller.role === 'seller' ? 'Cook' : 'Vendor'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="budget-min">Min Budget (₹)</Label>
                <Input
                  id="budget-min"
                  type="number"
                  placeholder="Optional"
                  value={requestForm.budget_min}
                  onChange={(e) => setRequestForm(prev => ({ ...prev, budget_min: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="budget-max">Max Budget (₹)</Label>
                <Input
                  id="budget-max"
                  type="number"
                  placeholder="Optional"
                  value={requestForm.budget_max}
                  onChange={(e) => setRequestForm(prev => ({ ...prev, budget_max: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowRequestModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleRequestSubmit}
                disabled={requestLoading}
              >
                {requestLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </BuyerLayout>
  );
}
